import type { VocabWord } from 'modules/vocabulary';

/** Cards per session. */
export const DECK_SIZE = 20;

/** A missed card is shown once more in the same session; missing it again settles it as "to repeat". */
export interface SessionState {
  deck: readonly VocabWord[];
  /** Ids still to show, the current card first. */
  queue: readonly string[];
  /** Ids that were missed once and have been put back at the end of the queue. */
  requeued: readonly string[];
  learned: readonly string[];
  missed: readonly string[];
  revealed: boolean;
}

/** The learner's words that are not learned yet, in list order, up to the session cap. */
export function buildDeck(words: readonly VocabWord[], size: number = DECK_SIZE): VocabWord[] {
  return words.filter((word) => !word.learned).slice(0, size);
}

export function startSession(deck: readonly VocabWord[]): SessionState {
  return { deck, queue: deck.map((word) => word.id), requeued: [], learned: [], missed: [], revealed: false };
}

export function currentWord(state: SessionState): VocabWord | undefined {
  const id = state.queue[0];
  return state.deck.find((word) => word.id === id);
}

export const isFinished = (state: SessionState): boolean => state.queue.length === 0;

/** Cards settled so far (known, or missed twice) out of the whole deck. */
export function progress(state: SessionState): { done: number; total: number } {
  return { done: state.learned.length + state.missed.length, total: state.deck.length };
}

export function reveal(state: SessionState): SessionState {
  return isFinished(state) || state.revealed ? state : { ...state, revealed: true };
}

/** "Bilaman" (knew) or "Bilmayman" (did not). Ignored until the card has been flipped. */
export function answer(state: SessionState, knew: boolean): SessionState {
  if (!state.revealed || isFinished(state)) return state;
  const [id, ...rest] = state.queue;
  if (knew) return { ...state, queue: rest, learned: [...state.learned, id], revealed: false };
  if (state.requeued.includes(id)) return { ...state, queue: rest, missed: [...state.missed, id], revealed: false };
  return { ...state, queue: [...rest, id], requeued: [...state.requeued, id], revealed: false };
}

/** How many cards were learned, and the words to replay. */
export function summary(state: SessionState): { learned: number; toRepeat: VocabWord[] } {
  return { learned: state.learned.length, toRepeat: state.deck.filter((word) => state.missed.includes(word.id)) };
}
